import {
    useEffect,
    useState } from "react";
import { useSelector } from "react-redux";
import "./ManageEcart.css"
import useUiController from "../global/hooks/useUiController";
import SnpBaseLayout from "../global/SnpBaseLayout";
import { Autocomplete,
    Box,
    Grid,
    Paper,
    TextField,
    Typography } from "../ui";
import SnpButton from "../global/SnpButton";
import SlideshowIcon from '../ui/icons/Slideshow';
import ShoppingCartCheckoutIcon from '../ui/icons/ShoppingCartCheckout';
import {
  getAuthSession,
  CartDets,
  ECARTWEBSTORELINK,
  getPreUrlAccordingToCart,
  GETSINGLECONFIGS,
  isEmpty,
  isFeatureAllowed,
  isNumberEmpty,
  printDebugLog,
  SUCCESS,
  THEMEMGMT
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import axios from "axios";
import TableRestaurantIcon from '../ui/icons/TableRestaurant';
import TableViewIcon from '../ui/icons/TableView';
import utilities from "../../utilities/utilities";
import { isSupported } from "firebase/messaging";
import { requestTestBaseFcmNotification, setBaseFcmNotification } from "../dashboard/dashboardNetworkController";
import { requestFCMToken, unsubscribeFCM } from "../../firebase";
import SnpSwitch from "../global/SnpSwitch";
import { ENABLE_NOTIFICATION_SOUND, NOTIFICATION_SOUND_NEXT_INTERVAL, SnpLocalStorage } from "../../utilities/snpLocalStorage";

const ThemeManager = () => {

  const storeActiveShopHash = useSelector(state => state.setActiveShopHashReducer.activeShopHash);
  const storeApiUrl = useSelector(state => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL);

  const {
    loader,
    setLoader,
    snackbarConfig,
    closeSnackbar,
    dialogConfig,
  } = useUiController();

  const [listTableData, setListTableData] = useState([]);
  const [selectedTableData, setSelectedTableData] = useState(null);
  const [premiumUrl, setPremiumUrl] = useState("");
  const [fcmSupporting, setFcmSupport] = useState(false);
  const [browserNotificationSubscribed, setBrowserNotificationSubscribed] = useState(false);
  const [notificationSound, setNotificationSound] = useState(true);
  const [notificationInterval, setNotificationInterval] = useState(8000);
  const [openKitchenAfterLogin, setOpenKitchenAfterLogin] = useState(getAuthSession().openKitchenAfterLogin);
  const [kitchenNotificationTray, setKitchenNotificationTray] = useState(getAuthSession().kitchenNotificationTray);
  const [kitchenNotificationSound, setKitchenNotificationSound] = useState(getAuthSession().kitchenNotificationSound);
  const activeChefValue = storeActiveShopHash?.isChef ?? getAuthSession().isChef;
  const isChefEnabled = Number(storeActiveShopHash?.cartType) === CartDets.Resto
    && (activeChefValue === true || activeChefValue === 'true' || activeChefValue === 1 || activeChefValue === '1');
  const themeManagementAllowed = isFeatureAllowed(THEMEMGMT);

  const OPEN_KITCHEN_AFTER_LOGIN = 'Open_Kitchen_After_Login';
  const KITCHEN_NOTIFICATION_TRAY = 'Kitchen_Notification_Tray';
  const KITCHEN_NOTIFICATION_SOUND = 'Kitchen_Notification_Sound';

  useEffect(() => {
    if (themeManagementAllowed) {
      getTableDetailsData();
      getSingleConfigs(ECARTWEBSTORELINK);
    }
    (async () => {
      const supported = await isSupported();
      setFcmSupport(supported);
    })();
    setBrowserNotificationSubscribed('Notification' in window && Notification.permission === 'granted');
    setNotificationSound(SnpLocalStorage.get(ENABLE_NOTIFICATION_SOUND, true));
    const val = SnpLocalStorage.get(NOTIFICATION_SOUND_NEXT_INTERVAL, 8000);
    setNotificationInterval(val / 1000);
    setOpenKitchenAfterLogin(storeActiveShopHash?.openKitchenAfterLogin ?? getAuthSession().openKitchenAfterLogin);
    setKitchenNotificationTray(storeActiveShopHash?.kitchenNotificationTray ?? getAuthSession().kitchenNotificationTray);
    setKitchenNotificationSound(storeActiveShopHash?.kitchenNotificationSound ?? getAuthSession().kitchenNotificationSound);
  }, [storeActiveShopHash, storeApiUrl]);

  const updateKitchenSetting = async (configName, enabled, setValue, stateKey) => {
    const previous = getAuthSession[stateKey];
    if (configName === KITCHEN_NOTIFICATION_TRAY && enabled && 'Notification' in window) {
      const permission = Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
      if (permission !== 'granted') return;
    }
    setValue(enabled);
    try {
      const configResponse = await snpAxios.get(`${storeApiUrl.BACKEND_BASE_URL}resto/get-single-full-configs`, {
        params: {
          adminId: getAuthSession().apiToken,
          configName,
          shopHash: storeActiveShopHash.id,
          prefix: '',
        },
      });
      const config = configResponse.data?.jcJson;
      if (!config?.id) throw new Error('Kitchen notification configuration is missing');
      const response = await snpAxios.post(`${storeApiUrl.BACKEND_BASE_URL}resto/persist-configs`, {
        jcJson: {
          apiToken: getAuthSession().apiToken,
          shopHash: storeActiveShopHash.id,
          id: config.id,
          configName,
          prefix: config.prefix || '',
          comments: config.comments || '',
          val: enabled ? 'Yes' : 'No',
          allowed: config.allowed ?? true,
        },
      });
      if (response.data.status !== SUCCESS) throw new Error('Kitchen notification configuration update failed');
      getAuthSession[stateKey] = enabled;
    } catch (error) {
      console.error('Unable to save kitchen notification setting', error);
      setValue(previous);
    }
  };

  const getSingleConfigs = async (mConfigName) => {
    const cUrl = `${storeApiUrl.BACKEND_BASE_URL}${getPreUrlAccordingToCart(storeActiveShopHash.cartType)}${GETSINGLECONFIGS}?adminId=${getAuthSession().apiToken}&configName=${mConfigName}&shopHash=${storeActiveShopHash.id}`;

    try {
      const response = await axios.get(cUrl);
      printDebugLog(`getSingleConfigs: ${mConfigName}`, response.data);
      const configValue = response.data;

      if (configValue) {
        switch (mConfigName) {
          case ECARTWEBSTORELINK:
            setPremiumUrl(configValue);
            break;
          default:
            console.warn(`Unhandled config name: ${mConfigName}`);
        }
      }
    } catch (error) {
      console.error("Error fetching configuration: ", error);
      // Optionally redirect or handle error UI
      // window.location.href = "#/error";
    }
  };

  const getTableDetailsData = async () => {
    try {
      setLoader(true);
      const cartUrl = `${storeApiUrl.BACKEND_BASE_URL}resto/get-table-data?shopHash=${storeActiveShopHash.id}&apiToken=${getAuthSession().apiToken}`;
      const response = await snpAxios.get(cartUrl);
      if (response.data.status === SUCCESS) {
        const payLoad = response.data.jcJson;
        setListTableData(payLoad.data);
      }
    } catch (error) {
      console.error("getTableDetailsData", error);
    } finally {
      setLoader(false);
    }
  };


  const genQrCode = async (mUrl, hash) => {
    try {
      setLoader(true);
      const shopHash = utilities.encode(storeActiveShopHash.id);
      let tableUrl = "";
      if (!isEmpty(hash)) {
        hash = utilities.encode(hash);
        tableUrl = `&tableHash=${hash}`;
      }
      const cUrl = `${storeApiUrl.BACKEND_BASE_URL}cart/${mUrl}?shopHash=${shopHash}${tableUrl}`;
      const response = await axios.get(cUrl);
      if (response.data.status === SUCCESS) {
        const url = `${storeApiUrl.BACKEND_BASE_URL}files/${response.data.jcJson.path}`;
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error("genQrCode error : ", error);
    } finally {
      setLoader(false);
    }
  }


  const handleSelectChange = (event, tableDataRow) => {
    //printLog("handleSelectChange", tableDataRow);
    if (tableDataRow != null) {
      setSelectedTableData(tableDataRow);
    } else {
      setSelectedTableData(null);
    }
  };


  const getTableName = (option) => {
    if (option) {
      if (!isEmpty(option.tableName)) {
        return `Table : ${option.tableNo} ${option.tableName}`;
      }
      return `Table : ${option.tableNo}`;
    }
    return "";
  };

  const handlePreviewEcart = (isPremium, key, hash) => {
    const encodedString = utilities.encode(hash);
    if (isPremium) {
      const strUrl = `${premiumUrl}/shop?${key}=${encodedString}`;
      //const strUrl = `http://localhost:3001/shop?${key}=${encodedString}`;
      console.log("handlePreviewEcart", strUrl);
      window.open(strUrl, "_blank", "noreferrer");
    } else {
      window.open(`${window.location.origin}#/shop?/${key}=${encodedString}`, "_blank", "noreferrer");
    }
  };

  const handleRequestNotification = async () => {
    if (browserNotificationSubscribed) {
      setLoader(true);
      unsubscribeFCM();
      setBaseFcmNotification(getAuthSession().apiToken, storeApiUrl.BACKEND_BASE_URL, storeActiveShopHash, "", (_) => {
        setBrowserNotificationSubscribed(false);
        setLoader(false);
      });
    } else {
      const t = await requestFCMToken();
      if (t) {
        setBaseFcmNotification(getAuthSession().apiToken, storeApiUrl.BACKEND_BASE_URL, storeActiveShopHash, t, (passed) => {
          setBrowserNotificationSubscribed(passed);
          setLoader(false);
        });
      } else {
        setLoader(false);
      }
    }
  }

  return (
    <SnpBaseLayout
      loader={loader}
      snackbarConfig={{ ...snackbarConfig, onClose: closeSnackbar }}
      dialogConfig={dialogConfig}
    >
      <div className="status-block">
        <div className="status-header" style={{ borderLeftColor: "#ef6c00" }}>
          <h3>Kitchen Notification Settings</h3>
        </div>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Paper elevation={10} sx={{ backgroundColor: "#fff7ed", boxShadow: 1, borderRadius: 4, padding: 2 }}>
              {isChefEnabled ?
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SnpSwitch leftLabel="Do not open after login" rightLabel="Open kitchen after login"
                      checked={openKitchenAfterLogin}
                      onChange={(event) => updateKitchenSetting(OPEN_KITCHEN_AFTER_LOGIN, event.target.checked,
                        setOpenKitchenAfterLogin, 'openKitchenAfterLogin')} color="primary" spacing={2} />
                  </Grid>
                  <Grid item xs={12}>
                    <SnpSwitch leftLabel="Disable tray notification" rightLabel="Enable tray notification"
                      checked={kitchenNotificationTray}
                      onChange={(event) => updateKitchenSetting(KITCHEN_NOTIFICATION_TRAY, event.target.checked,
                        setKitchenNotificationTray, 'kitchenNotificationTray')} color="primary" spacing={2} />
                  </Grid>
                  <Grid item xs={12}>
                    <SnpSwitch leftLabel="Mute kitchen sound" rightLabel="Enable kitchen sound"
                      checked={kitchenNotificationSound}
                      onChange={(event) => updateKitchenSetting(KITCHEN_NOTIFICATION_SOUND, event.target.checked,
                        setKitchenNotificationSound, 'kitchenNotificationSound')} color="primary" spacing={2} />
                  </Grid>
                </Grid> :
                <p>Enable “Handle Kitchen Order” for this user to manage kitchen notifications.</p>
              }
            </Paper>
          </Grid>
        </Grid>
        {themeManagementAllowed && <>
        <div className="status-header" style={{ borderLeftColor: "#00bcd4" }}>
          <h3>Online Ecart Management</h3>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={10}
              sx={{
                display: "flex",
                flexDirection: "row",
                backgroundColor: "#e6e7e8",
                boxShadow: 1,
                borderRadius: 4,
                padding: 2,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} sm={6} lg={6}>
                  <SnpButton
                    variant="contained"
                    mbgcolor="#543d4e"
                    mheight="32px"
                    mborderradius="14px"
                    style={{ float: "center" }}
                    onClick={() => genQrCode("generate-shop-qr-premium", "")}
                  >
                    <ShoppingCartCheckoutIcon /> Generate Online web portal
                  </SnpButton>
                </Grid>
                <Grid item xs={12} md={6} sm={6} lg={6}>
                  <SnpButton
                    variant="contained"
                    mbgcolor="#7c6c78ff"
                    mheight="32px"
                    mborderradius="14px"
                    style={{ float: "center" }}
                    onClick={() => handlePreviewEcart(true, 'q', storeActiveShopHash.id)}
                  >
                    <SlideshowIcon /> Preview Online Web Portal
                  </SnpButton>
                </Grid>
                {
                  (listTableData && listTableData.length > 1)
                  &&
                  <Grid item xs={12} md={5} sm={6}>
                    <Autocomplete
                      id="pOnline-select"
                      options={listTableData}
                      autoHighlight
                      variant="filled"
                      size="small"
                      onChange={handleSelectChange}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      getOptionLabel={(option) => getTableName(option)}
                      value={selectedTableData}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          key={option?.id ?? ""}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "left",
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: "#e0f7fa",
                            },
                            "&.Ui-focused": {
                              backgroundColor: "#b2ebf2",
                            },
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#01333E" }}>
                            Table {option.tableNo}
                          </Typography>
                          {option.tableName && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {option.tableName}
                            </Typography>
                          )}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Table"
                          variant="filled"
                          InputLabelProps={{
                            sx: {
                              color: "#01333E",
                              fontWeight: 600,
                            },
                          }}
                          sx={{
                            "& .UiFilledInput-root": {
                              backgroundColor: "#f0f4f5",
                              borderRadius: 1,
                            },
                            "& .UiInputBase-input": {
                              fontWeight: 500,
                            },
                          }}
                        />
                      )}
                      sx={{
                        "& .UiAutocomplete-paper": {
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          backgroundColor: "#ffffff",
                        },
                        "& .UiAutocomplete-option": {
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                  </Grid>

                }
                {
                  !isEmpty(selectedTableData?.id ?? "") &&
                  <Grid item xs={12} md={5} sm={5} lg={5}></Grid>
                }
                {
                  !isEmpty(selectedTableData?.id ?? "") &&
                  <Grid item xs={12} md={6} sm={6} lg={6}>
                    <SnpButton
                      variant="contained"
                      mbgcolor="#543d4e"
                      mheight="32px"
                      mborderradius="14px"
                      style={{ float: "center" }}
                      onClick={() => genQrCode("generate-table-qr-premium", selectedTableData?.id ?? "")}

                    >
                      <TableRestaurantIcon /> Generate Table Qr Code
                    </SnpButton>
                  </Grid>
                }
                {
                  !isEmpty(selectedTableData?.id ?? "") &&
                  <Grid item xs={12} md={6} sm={6} lg={6}>
                    <SnpButton
                      variant="contained"
                      mbgcolor="#7c6c78ff"
                      mheight="32px"
                      mborderradius="14px"
                      style={{ float: "center" }}
                      onClick={() => handlePreviewEcart(true, 't', selectedTableData?.id ?? "")}
                    >
                      <TableViewIcon /> Preview Table
                    </SnpButton>
                  </Grid>
                }
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <div className="status-header" style={{ marginTop: "20px", borderLeftColor: "#00bcd4" }}>
          <h3>Notification Management</h3>
        </div>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Paper
              elevation={10}
              sx={{
                display: "flex",
                flexDirection: "row",
                backgroundColor: "#e6e7e8",
                boxShadow: 1,
                borderRadius: 4,
                padding: 2,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {
                    fcmSupporting ?
                      <SnpButton
                        variant="contained"
                        mbgcolor={browserNotificationSubscribed ? "#dd706e" : "#7c6c78ff"}
                        mheight="32px"
                        mborderradius="14px"
                        style={{ float: "center" }}
                        onClick={() => handleRequestNotification()}
                      >
                        {browserNotificationSubscribed ? "Unsubscribe for notification" : "Subscribe for Notification"}
                      </SnpButton> :
                      <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>Your browser don't support Notification tray</p>
                  }
                </Grid>
                {
                  browserNotificationSubscribed &&
                  <Grid item xs={12}>
                    <SnpButton
                      variant="contained"
                      mbgcolor={"#2a2031ff"}
                      mheight="32px"
                      mborderradius="14px"
                      style={{ float: "center" }}
                      onClick={() => requestTestBaseFcmNotification(getAuthSession().apiToken, storeApiUrl.BACKEND_BASE_URL,
                        storeActiveShopHash, setLoader
                      )}
                    >
                      Test Notification
                    </SnpButton>
                  </Grid>
                }
                {
                  browserNotificationSubscribed &&
                  <Grid item xs={12}>
                    <SnpSwitch
                      leftLabel="Disable Sound"
                      rightLabel="Enable Sound"
                      checked={notificationSound}
                      onChange={(e) => {
                        SnpLocalStorage.set(ENABLE_NOTIFICATION_SOUND, e.target.checked);
                        setNotificationSound(e.target.checked);
                      }}
                      color="primary"
                      spacing={2}
                    />
                  </Grid>
                }
                {
                  browserNotificationSubscribed &&
                  <Grid item xs={12}>
                    <TextField
                      id="notificationInterval"
                      label="Enter Notification interval in second"
                      name="notificationInterval"
                      helperText={"Default 8 second"}
                      variant="filled"
                      size="small"
                      type="number"
                      value={notificationInterval}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!isNumberEmpty(val)) {
                          const parseVal = Number(val);
                          const valMs = parseVal * 1000;
                          setNotificationInterval(parseVal);
                          SnpLocalStorage.set(NOTIFICATION_SOUND_NEXT_INTERVAL, valMs);
                        }
                      }}
                    />
                  </Grid>
                }
              </Grid>
            </Paper>
          </Grid>

        </Grid>
        </>}
      </div>

    </SnpBaseLayout>
  )
};

export default ThemeManager;
